import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail, ROLE_LABELS } from "@/lib/api";
import { PageHeader, Section, PrimaryButton } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Eye, EyeOff, KeyRound, Sparkles, Power } from "lucide-react";

export default function MonCompte() {
  const { user, setUser } = useAuth();
  const isVendeur = user?.role === "vendeur";
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  // IA
  const [ai, setAi] = useState({ provider_pref: "shared", has_own_key: false, own_model: "gpt-5.2" });
  const [newKey, setNewKey] = useState("");
  const [savingAi, setSavingAi] = useState(false);

  // Disponibilité
  const [dispo, setDispo] = useState(true);
  const [savingDispo, setSavingDispo] = useState(false);

  useEffect(() => {
    api.get("/users/me/ai-settings").then((r) => setAi(r.data)).catch(() => {});
    if (user) setDispo(user.accepte_prospects !== false);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères.");
    if (next !== confirm) return toast.error("La confirmation ne correspond pas au nouveau mot de passe.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe modifié avec succès.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveAi = async (patch) => {
    setSavingAi(true);
    try {
      const { data } = await api.put("/users/me/ai-settings", patch);
      setAi(data);
      setNewKey("");
      toast.success("Réglages IA enregistrés.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingAi(false);
    }
  };

  const toggleDispo = async () => {
    const nextVal = !dispo;
    setSavingDispo(true);
    try {
      await api.put("/users/me/disponibilite", { accepte: nextVal });
      setDispo(nextVal);
      if (setUser && user) setUser({ ...user, accepte_prospects: nextVal });
      toast.success(nextVal ? "Tu recevras de nouveaux prospects." : "Tu ne recevras plus de nouveaux prospects. L'administration en est informée.");
    } catch (err) {
      toast.error("Modification impossible.");
    } finally {
      setSavingDispo(false);
    }
  };

  const field = "w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#B8873A] focus:border-[#B8873A]";

  return (
    <div className="p-8" data-testid="mon-compte-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="Mon compte" subtitle="Vos informations et sécurité" />

      <div className="max-w-xl space-y-6">
        <Section title="Informations">
          <div className="p-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Nom</span><span className="font-medium" data-testid="account-name">{user?.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Identifiant</span><span className="font-medium" data-testid="account-email">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Rôle</span><span className="font-medium">{ROLE_LABELS[user?.role] || user?.role}</span></div>
          </div>
        </Section>

        {isVendeur && (
          <Section title="Disponibilité">
            <div className="p-5" data-testid="disponibilite-section">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-[#111111] flex items-center gap-2"><Power className="w-4 h-4 text-[#B8873A]" /> Recevoir de nouveaux prospects</div>
                  <p className="text-sm text-slate-500 mt-1">Désactive si tu ne souhaites plus recevoir de nouveaux leads (vacances, charge pleine…). L'administration le voit et ne t'attribue plus de prospects.</p>
                </div>
                <button
                  onClick={toggleDispo}
                  disabled={savingDispo}
                  data-testid="disponibilite-toggle"
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${dispo ? "bg-[#B8873A]" : "bg-slate-300"} disabled:opacity-50`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${dispo ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              <div className="mt-3 text-xs font-semibold" data-testid="disponibilite-status">
                {dispo ? <span className="text-emerald-700">● Disponible — tu reçois des prospects</span> : <span className="text-slate-500">● Indisponible — tu ne reçois plus de prospects</span>}
              </div>
            </div>
          </Section>
        )}

        <Section title="Mon assistant IA">
          <div className="p-5 space-y-4" data-testid="ai-settings-section">
            <p className="text-sm text-slate-500 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#B8873A]" /> Choisis l'IA utilisée par ton assistant.</p>

            <label className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${ai.provider_pref === "shared" ? "border-[#B8873A] bg-[#FBFAF7]" : "border-slate-200"}`} data-testid="ai-pref-shared">
              <input type="radio" name="ai-pref" checked={ai.provider_pref === "shared"} onChange={() => saveAi({ provider_pref: "shared" })} className="mt-1 accent-[#B8873A]" />
              <div>
                <div className="font-medium text-[#111111]">IA partagée de l'agence <span className="text-xs text-slate-400">(recommandé)</span></div>
                <div className="text-sm text-slate-500">Claude + ChatGPT déjà inclus, rien à installer. Limité à 10 messages/jour.</div>
              </div>
            </label>

            <label className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${ai.provider_pref === "own" ? "border-[#B8873A] bg-[#FBFAF7]" : "border-slate-200"}`} data-testid="ai-pref-own">
              <input type="radio" name="ai-pref" checked={ai.provider_pref === "own"} onChange={() => { if (ai.has_own_key) saveAi({ provider_pref: "own" }); else setAi({ ...ai, provider_pref: "own" }); }} className="mt-1 accent-[#B8873A]" />
              <div className="flex-1">
                <div className="font-medium text-[#111111]">Ma propre clé OpenAI (ChatGPT perso)</div>
                <div className="text-sm text-slate-500">Utilise ton compte OpenAI (facturé chez toi), sans limite de l'agence. {ai.has_own_key && <span className="text-emerald-700 font-medium">Clé enregistrée ✓</span>}</div>
              </div>
            </label>

            {ai.provider_pref === "own" && (
              <div className="pl-3 space-y-2">
                <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold">Clé API OpenAI (sk-…)</label>
                <input type="password" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder={ai.has_own_key ? "•••••••• (clé enregistrée — saisir pour remplacer)" : "sk-…"}
                  autoCapitalize="none" autoCorrect="off" spellCheck={false} className={field} data-testid="openai-key-input" />
                <p className="text-xs text-slate-400">Ta clé est stockée de façon sécurisée et n'est jamais affichée. Obtiens-la sur platform.openai.com → API keys.</p>
                <div className="flex gap-2">
                  <PrimaryButton onClick={() => saveAi({ provider_pref: "own", openai_api_key: newKey })} disabled={savingAi || !newKey.startsWith("sk-")} data-testid="save-openai-key">
                    {savingAi ? "…" : "Enregistrer ma clé"}
                  </PrimaryButton>
                  {ai.has_own_key && (
                    <button onClick={() => saveAi({ openai_api_key: "" })} disabled={savingAi} className="text-sm text-red-600 hover:underline" data-testid="remove-openai-key">
                      Supprimer ma clé
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section title="Changer mon mot de passe">
          <form onSubmit={submit} className="p-5 space-y-4" data-testid="change-password-form">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Mot de passe actuel</label>
              <input type={show ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} required
                autoCapitalize="none" autoCorrect="off" spellCheck={false} className={field} data-testid="current-password-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Nouveau mot de passe</label>
              <input type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} required
                autoCapitalize="none" autoCorrect="off" spellCheck={false} className={field} data-testid="new-password-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Confirmer le nouveau mot de passe</label>
              <input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                autoCapitalize="none" autoCorrect="off" spellCheck={false} className={field} data-testid="confirm-password-input" />
            </div>
            <button type="button" onClick={() => setShow((s) => !s)} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800" data-testid="toggle-password-visibility">
              {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {show ? "Masquer" : "Afficher"} les mots de passe
            </button>
            <div className="pt-2">
              <PrimaryButton type="submit" disabled={saving} data-testid="submit-change-password">
                <span className="flex items-center gap-2"><KeyRound className="w-4 h-4" /> {saving ? "Modification…" : "Modifier le mot de passe"}</span>
              </PrimaryButton>
            </div>
          </form>
        </Section>
      </div>
    </div>
  );
}
